<template>
  <div>
    <div class="filedrop subtitle-1"
         :class="cssClasses"
         v-ripple
         @dragover.prevent
         @dragenter="setDrag()"
         @dragleave="setDrag(false)"
         @drop.prevent="dropFile"
         @click="openDialog">
      <input ref="fileinput" class="filedrop-fileinput" type="file" multiple="multiple" @click="addFilesOnClick" @change="filesAdded"/>
      <div ref="content" class="filedrop-content">
        <slot>
          <v-btn :loading="isLoading" text tile class="filedrop-content-icon mr-2">
            <v-icon class="filedrop-defaultIcon" color="primary">cloud_upload</v-icon>
            <span>{{ labelValue }}</span>
          </v-btn>
        </slot>
      </div>
    </div>
    <v-alert v-for="errorState in errorStateList" style="padding: 6px; margin-top:12px; color: #B71C1C;background-color: #FFEBEE"
             v-if="errorState.fileSizeExceeded || errorState.invalidFileExtension || errorState.restrictedFileExtension">
      <div style="display: flex;align-items: center; justify-content: space-between;">
        <v-btn color="primary" style="float: left; flex: 1;" icon small>
          <v-icon style="color:#B71C1C;">mdi-alert</v-icon>
        </v-btn>
        <div style="display: flex; flex-direction: column; flex: 9;">
          <p style="margin-bottom: 4px !important;" v-if="errorState.fileSizeExceeded">{{ $vopsui.labels.filedrop.fileSizeExceeded(maxSizeInMb) }}</p>
          <p style="margin-bottom: 4px !important;" v-if="errorState.invalidFileExtension">{{ $vopsui.labels.filedrop.invalidFileExtension(extensions) }}</p>
          <p style="margin-bottom: 0px !important;" v-if="errorState.restrictedFileExtension">{{ $vopsui.labels.filedrop.restrictedFileExtension(errorState.fileName) }}</p>
        </div>
      </div>
    </v-alert>
  </div>
</template>

<script lang="ts">
import Vue from "vue";
import {Component, Prop, Watch, Emit} from "vue-property-decorator";
class ErrorState{
  fileName: string;
  fileSizeExceeded: boolean = false;
  invalidFileExtension: boolean = false;
  restrictedFileExtension: boolean = false;

  constructor(fileName: string = "", fileSizeExceeded: boolean = false, invalidFileExtension: boolean = false, restrictedFileExtension: boolean = false) {
    this.fileName = fileName;
    this.fileSizeExceeded = fileSizeExceeded;
    this.invalidFileExtension = invalidFileExtension;
    this.restrictedFileExtension = restrictedFileExtension;
  }
}
@Component
export default class Filedrop extends Vue {
  @Prop({type: String}) label: string;
  @Prop({type: String}) extensions: string;
  @Prop({type: Number, default: 30000000}) maxSize: number;

  @Emit() dropped(files: File[]) {
  }

  isDragging: boolean = false;
  isDropping: boolean = false;
  isLoading: boolean = false;
  restrictedExtensionList: string[] = ["bat", "exe", "cmd", "sh", "php", "pl", "cgi",
    "386", "dll", "com", "torrent", "js", "app", "jar", "pif", "vb", "vbscript", "wsf", "asp", "cer", "csr",
    "jsp", "drv", "sys", "ade", "adp", "bas", "chm", "cpl", "crt", "csh", "fxp", "hlp", "hta", "inf", "ins", "isp",
    "jse", "htaccess", "htpasswd", "ksh", "lnk", "mdb", "mde", "mdt", "mdw", "msc", "msi", "msp", "mst", "ops",
    "pcd", "prg", "reg", "scr", "sct", "shb", "shs", "url", "vbe", "vbs", "wsc", "wsf", "wsh"];
  currentFileName: string = "";
  errorStateList: ErrorState[] = [];
  get cssClasses() {
    return {
      'drag': this.isDragging,
      'drop': this.isDropping
    }
  }

  get labelValue() {
    return this.label || this.$vopsui.labels.filedrop.addFiles();
  }

  get maxSizeInMb() {
    return (this.maxSize / 1000000).toString();
  }

  openDialog() {
    (this.$refs.fileinput as HTMLElement).click();
  }

  setDrag(isDragging: boolean = true) {
    this.isDragging = isDragging;
  }

  dropFile(e: DragEvent) {
    this.setDrag(false);

    if (e.dataTransfer.files.length === 0)
      return;

    this.handleFiles(e.dataTransfer.files);
  }

  filesAdded(e: Event) {
    const $el = e.target as HTMLInputElement;
    this.handleFiles($el.files);
    $el.value = ''; // Reset the input value to allow re-uploading the same file
  }

  addFilesOnClick()
  {
      this.errorStateList = [];  
  }

  handleFiles(filelist: FileList) {
    const files = this.getValidFiles(filelist);
    if (files.length === 0)
      return;

    this.dropped(files);

    const $el = this.$refs.content as Element;
    this.isDropping = true;
    this.isLoading = true;
    $el.addEventListener('transitionend', e => {
      setTimeout(() => {
        this.isDropping = false;
        this.isLoading = false;
      }, 1000);
    }, {once: true});
  }

  private getValidFiles(fileList: FileList) {
    const files = Array.from(fileList);

    const errorStateMap = new Map<string, ErrorState>();
    const validFiles: File[] = [];

    for (const file of files) {
      let fileSizeError = file.size >= this.maxSize;
      let restrictedFileExtensionError = this.restrictedExtensionList.includes(this.getExtension(file));

      const exts: RegExpMatchArray = (this.extensions || "").match(/\w+/g);
      var validFileExtension = !exts || exts.includes(this.getExtension(file));

      let invalidFileExtensionError = !validFileExtension;

      if (fileSizeError || invalidFileExtensionError || restrictedFileExtensionError) {
        let errorState = errorStateMap.get(file.name);
        if (!errorState) {
          errorState = new ErrorState(file.name);
          errorStateMap.set(file.name, errorState);
        }

        errorState.fileSizeExceeded = fileSizeError;
        errorState.invalidFileExtension = invalidFileExtensionError;
        errorState.restrictedFileExtension = restrictedFileExtensionError;
        this.errorStateList.push(errorState);
      } else {
        validFiles.push(file);
      }
    }
    return validFiles;
  }

  private getExtension(file: File) {
    return file.name.split('.').pop();
  }
}
</script>

<style lang="scss">
.filedrop {
  display: flex;
  flex-wrap: wrap;
  outline: dashed 2px var(--border-color);
  justify-content: center;
  align-items: center;
  height: 48px;
  position: relative;
  background-color: transparent;
  transition: background-color 0.25s;
  overflow: hidden;
  cursor: pointer;

  .theme--light & {
    --drag-color: var(--v-grey-lighten2);
    --drop-color: var(--v-primary-lighten1);
  }

  .theme--dark & {
    --drag-color: var(--v-grey-darken1);
    --drop-color: var(--v-primary-lighten1);
  }

  &.drag {
    background-color: var(--drag-color);
    transition: background-color 0.25s;
  }

  &.drop {
  }
}

.filedrop-fileinput {
  opacity: 0;
  z-index: -1;
  position: absolute;
}

.filedrop-content {
  display: flex;
}

.filedrop-content-icon {
  width: 24px;
  position: relative;
  transition: transform 0.25s;

  .filedrop.drop & {
    //transform: rotate(360deg);
  }
}

.filedrop-defaultIcon {
  position: absolute;
  opacity: 1;
  transition: opacity 0.25s;
  margin-right: 10px;

  .filedrop.drop & {
    opacity: 0;
  }
}

.filedrop-content-icon.theme--light.v-btn:hover::before {
  opacity: 0;
}

.filedrop-content-icon.theme--light.v-btn {
  background-color: transparent;
}


</style>